import React, { useMemo, useState } from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import { frozenBodyOf, invoiceRenderRequest } from '../../domain/documentRender';
import { format, formatInParentheses } from '../../domain/money';
import { invoiceNegativesInParentheses } from '../../domain/invoice';
import { useLocale } from '../../context/LocaleContext';
import { LOGO_WORDING } from '../../domain/logo';
import { documentPdfWord } from '../../domain/documentPdf';
import { openDocumentPdf } from '../../data/documentLinks';
import s from './InvoiceDocument.module.css';

// The invoice as the family sees it (SPEC.md §3.5).
//
// Rendered from the stored snapshot, never recomputed. Once an invoice is
// issued it has to keep showing the numbers it showed when it was sent, even if
// someone later back-dates a payment or corrects an attendance record — so this
// component reads only from the document and never folds the ledger itself.

// `studioName` is the studio's own name from Business Settings
// (data/useSettings.jsx). A studio that has not named itself still prints an
// invoice, under the fallback functions/delivery/facts.js uses on a receipt.
//
// Money is written by money.js's `format()` in the currency and Region the
// invoice's body names — the same call `useMoneyFormat` makes with the
// studio's, and exact: it builds a decimal string rather than dividing by
// 100, so 12345678901234567 minor units is $123,456,789,012,345.67. An
// invoice is the last document in the app that should carry a rounding error.
//
// ── What Business Settings → Accounts & Invoices changes here ──────────────
// `invoicing` is the normalised group from domain/studio.js. Five of its
// fields land on this document:
//
//   invoiceName            replaces the studio name in the From block. Null
//                          falls back to the studio name, which is why the
//                          field is nullable rather than defaulted — MMS keeps
//                          the two apart so a studio can invoice under a
//                          trading name without renaming itself everywhere.
//   footerText             the default footer. The invoice's own footerNote —
//                          typed per run in New Invoice — still wins, because
//                          that one was written about *this* invoice.
//   accentColor            tints the heading, the table header and the total.
//                          Interpolated into a style attribute, which is why
//                          the domain and the rules both insist it is a hex
//                          colour and not a free string.
//   negativeDisplay        what a credit balance reads as.
//   groupChargesByStudent  one heading row per student.
//   showLocation           the room a lesson was taught in, under its line.
//
// `lineDetail` supplies the last two: `(line) => { studentName, location }`,
// built by whoever has the ledger, the students and the locations to hand (see
// hooks/useInvoiceLineDetail.js). For an invoice that is not frozen those two
// facts are looked up live, so a renamed room shows its current name; a lookup
// that comes back empty leaves the line as it was. A frozen invoice (#622) is
// not looked up: its labels were frozen with it.

// ── Drawn from the body the PDF is drawn from (#622) ────────────────────────
//
// The page a teacher looks at must be the one the family is sent. So this draws
// from the renderer's body (src/domain/documentRender.js) — the invoice's frozen
// one, written when the invoice was created, with the logo it was drawn with
// (`logo`, useInvoiceLogo.js), its dates as written then and its money in the
// currency and Region it was issued in; and for an invoice that is not frozen,
// the same body built now from the studio's settings and the live line lookups
// above, which its PDF is drawn from too. Every decision on it — the heading,
// the footer, the due line, each line's columns and the student groups — was
// the core's when the body was made.
//
// ── Open PDF (#621) ─────────────────────────────────────────────────────────
//
// The button opens the server's PDF of the invoice: drawn by
// `services/report-pdf` from this snapshot and the same core decisions, kept in
// Storage, and handed out as a link that lasts thirty days — the file Android
// and the iPhone share. `documentLink` is the page's (data/documentLinks.js);
// with none, the button is not drawn.

export default function InvoiceDocument({
  open, onClose, invoice, familyName, studioName, invoicing, lineDetail, logo = null, locale = null, documentLink = null,
}) {
  // Before the early return: a hook after a conditional return is a different
  // hook order on the next render.
  const [pdf, setPdf] = useState({ busy: false, error: null, url: null, invoiceId: null });
  // A live invoice is written in the Region the rest of the screen is: the
  // studio's, as the locale context holds it, unless the page names one.
  const screenLocale = useLocale().locale;
  const liveLocale = locale || screenLocale || null;
  const body = useMemo(() => {
    if (!invoice) return null;
    return frozenBodyOf(invoice) || invoiceRenderRequest(invoice, {
      studioName, familyName, invoicing, locale: liveLocale, lineDetail,
    });
  }, [invoice, studioName, familyName, invoicing, liveLocale, lineDetail]);

  if (!invoice || !body) return null;
  const doc = body.invoice;
  // Money in the currency and Region the body names — the studio's for a live
  // invoice, as `useMoneyFormat` writes it, and the issued one's for a frozen
  // one. A negative figure is written as the invoice was issued: in
  // parentheses, `($10.00)`, where the body says so in `negatives` (#649);
  // with a minus otherwise.
  const inParentheses = invoiceNegativesInParentheses(doc.negatives);
  const formatMoney = (minor) => (inParentheses
    ? formatInParentheses(minor, body.studio.currency, body.studio.locale)
    : format(minor, body.studio.currency, body.studio.locale));

  // What the PDF button last said, for this invoice only.
  const pdfState = pdf.invoiceId === invoice.id ? pdf : { busy: false, error: null, url: null };
  const openPdf = () => {
    const id = invoice.id;
    setPdf({ busy: true, error: null, url: null, invoiceId: id });
    openDocumentPdf(documentLink, 'invoice', id).then(
      // A browser that gave no tab gets the link to click instead.
      ({ url, opened }) => setPdf({ busy: false, error: null, url: opened ? null : url, invoiceId: id }),
      (err) => setPdf({ busy: false, error: err?.message || documentPdfWord('failed'), url: null, invoiceId: id }),
    );
  };

  // A family in credit reads as the studio's Negative Display chose — "$0.00
  // due" or "Balance Remaining" — which `dueLabel` and `dueMinor` already are.
  const lineRow = (l, i) => (
    <tr key={i}>
      <td>{l.date}</td>
      <td>
        {l.description}
        {l.location ? <div className={s.lineLocation}>{l.location}</div> : null}
      </td>
      <td>{l.chargeMinor != null ? formatMoney(l.chargeMinor) : ''}</td>
      <td>{l.paymentMinor != null ? formatMoney(l.paymentMinor) : ''}</td>
    </tr>
  );

  return (
    <Drawer open={open} onClose={onClose} title="Invoice" width={780}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <FooterSpacer />
          {documentLink ? (
            <Button variant="secondary" onClick={openPdf} loading={pdfState.busy}>{documentPdfWord('open_pdf')}</Button>
          ) : null}
        </>
      }>

      {pdfState.error ? <div className={s.pdfNotice} role="alert">{pdfState.error}</div> : null}
      {pdfState.url ? (
        <div className={s.pdfNotice}>
          <a href={pdfState.url} target="_blank" rel="noopener noreferrer">{documentPdfWord('open_pdf')}</a>
        </div>
      ) : null}

      {/* The accent is a custom property on the document root rather than a
          colour on each element, so one declaration tints the heading, the
          table header and the totals, and the stylesheet keeps its own default
          for anything rendered without a studio record. */}
      <div className={s.doc} style={body.studio.accentColor ? { '--invoice-accent': body.studio.accentColor } : undefined}>
        <div className={s.top}>
          {/* The studio's logo, top left, where MMS's invoice has it (#498):
              the one a frozen invoice was drawn with, or the studio's own for an
              invoice that is not frozen. With none, the clef (#524). */}
          {logo
            ? <img className={s.logo} src={logo} alt={LOGO_WORDING.alt} />
            : <div className={s.clef} aria-hidden="true">𝄞</div>}
          <div className={s.topBody}>
            <div className={s.title}>INVOICE</div>
            <div className={s.meta}>Date: {doc.date}</div>
          </div>
        </div>

        <div className={s.parties}>
          <div>
            <div className={s.label}>From</div>
            <div className={s.studio}>{doc.from}</div>
          </div>
          <div>
            <div className={s.label}>Bill To</div>
            <div className={s.family}>{doc.billTo}</div>
          </div>
          <div className={s.summary}>
            <div><span>Previous Balance:</span><span>{formatMoney(doc.previousBalanceMinor)}</span></div>
            <div><span>Payments:</span><span>{formatMoney(doc.paymentsMinor)}</span></div>
            <div><span>New Charges:</span><span>{formatMoney(doc.newChargesMinor)}</span></div>
            <div className={s.total}>{doc.dueLabel}: {formatMoney(doc.dueMinor)}</div>
          </div>
        </div>

        <table className={s.table}>
          <thead><tr><th>Date</th><th>Description</th><th>Charges</th><th>Payments</th></tr></thead>
          <tbody>
            {(doc.groups || []).map((g, gi) => (
              // A group with no heading is the plain table.
              <React.Fragment key={gi}>
                {g.heading ? <tr className={s.groupRow}><th colSpan={4} scope="colgroup">{g.heading}</th></tr> : null}
                {(g.lines || []).map(lineRow)}
                {/* A student's group ends in its subtotal where the invoice's
                    body has one (#650): MMS's `Nguyen, Ava Subtotal`, bold, no
                    date, the figure in Charges. */}
                {g.subtotal ? (
                  <tr className={s.subtotalRow}>
                    <td />
                    <td>{g.subtotal.label}</td>
                    <td>{formatMoney(g.subtotal.minor)}</td>
                    <td />
                  </tr>
                ) : null}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        <div className={s.totalRight}>
          {doc.dueLabel}: {formatMoney(doc.dueMinor)}
        </div>

        {doc.footer ? <div className={s.footNote}>{doc.footer}</div> : null}
      </div>
    </Drawer>
  );
}
